import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { BrandImageCard } from '@components/Home/BrandImageCard';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { TireUpSellCard } from '@components/TireOffer/TireUpSellCard';
import { CustomerDataQualityProvider } from '@components/UserGroupDataQuality/CustomerDataQualityProvider';
import { UserGroupDataQualityPromptCustomer } from '@components/UserGroupDataQuality/UserGroupDataQualityPromptCustomer';
import { Trans } from '@lingui/react/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { brands } from 'noddi-types';
import type { UserSessionData } from 'noddi-web';
import { useBookingUserGroupId } from '../../../hooks/useBookingUserGroupId';
import { useHasBrandDiscounts } from '../../../hooks/useHasBrandDiscounts';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';
import { AcceptedActiveTireOffers } from '../components/AcceptedActiveTireOffers';
import { Cars } from '../components/Cars';
import { ContractCard } from '../components/ContractCard';
import { Discounts } from '../components/Discounts';
import { HomePageHeader } from '../components/HomePageHeader';
import { InviteFriendsHome } from '../components/InviteFriendsHome';
import { PendingBookingProposals } from '../components/PendingBookingProposals';
import { PendingTireOffers } from '../components/PendingTireOffers';
import { PreviousBookings } from '../components/PreviousBookings';
import { UpcomingBookings } from '../components/UpcomingBookings';

type Props = {
  userGroupId: number;
  userData: UserSessionData;
  hasTireHotel: boolean;
};

export const PersonalHomePage = ({ userGroupId, userData, hasTireHotel }: Props) => {
  const { currentBrand } = useCurrentBrand();
  const { bookingUserGroupId, isReady: isBookingUserGroupReady } = useBookingUserGroupId(userGroupId);

  const {
    isPending: isBookingsPending,
    data: bookings,
    error: bookingsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedBookingsForCustomer,
    input: {
      userGroupId: bookingUserGroupId,
      pageIndex: 0,
      pageSize: 1,
      brandDomains: [currentBrand]
    },
    queryConfig: { enabled: isBookingUserGroupReady }
  });
  const { hasBrandDiscounts, hasReferralProgram, isPending: isBrandPromotionsPending } = useHasBrandDiscounts();

  if (isBookingUserGroupReady && isBookingsPending) {
    return <NoddiLoadingScreenWithTrans />;
  }

  const isFirstTimeCustomer = bookings ? bookings.count === 0 : false;
  const showTireUpSellCard = currentBrand === brands.noddi || currentBrand === brands.dekkfix;

  return (
    <ContentWrapperCustomerApp hideBackButton width='lg'>
      <div className='flex flex-col gap-5 md:gap-16'>
        <HomePageHeader userData={userData} isFirstTimeCustomer={isFirstTimeCustomer} />
        <CustomerDataQualityProvider userGroupId={userGroupId} openWhenBlocked>
          <UserGroupDataQualityPromptCustomer />
        </CustomerDataQualityProvider>
        {bookingsError && <ApiErrorMessageWithTrans error={bookingsError} />}
        <div className='flex flex-col gap-6'>
          <PendingBookingProposals userGroupId={userGroupId} />
          {showTireUpSellCard && (
            <>
              <PendingTireOffers userGroupId={userGroupId} />
              <AcceptedActiveTireOffers userGroupId={userGroupId} />
            </>
          )}
          <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
            {isFirstTimeCustomer && (
              <div className='flex flex-col gap-2 md:gap-4'>
                <h2 className='text-xl font-bold text-primary md:text-3xl'>
                  <Trans>No bookings</Trans>
                </h2>
                <BrandImageCard />
              </div>
            )}

            {/* Normal users with multiple cars on tire hotel will only see cars list, but that is fine for now */}
            {(hasTireHotel || currentBrand === brands.tronderdekk) && <ContractCard />}

            <UpcomingBookings userGroupId={userGroupId} />

            <Cars userGroupId={userGroupId} />

            <PreviousBookings userGroupId={userGroupId} />

            {showTireUpSellCard && <TireUpSellCard />}
            {hasBrandDiscounts && !isBrandPromotionsPending && <Discounts userGroupId={userGroupId} />}
            {hasReferralProgram && !isBrandPromotionsPending && <InviteFriendsHome userGroupId={userGroupId} />}
          </div>
        </div>
      </div>
    </ContentWrapperCustomerApp>
  );
};
